import { ApiError } from "@/lib/api/client";
import { useI18n, useTranslate } from "@/lib/i18n";
import {
  PERMISSIONS,
  hasPermission,
  useSession,
} from "@/modules/auth/useSession";

/**
 * Turns anything thrown by `apiFetch` into a string safe to put in front of
 * staff mid-service.
 *
 * The reason this exists: a dropped connection makes `fetch` throw a
 * `TypeError` whose message is the browser's own `"Failed to fetch"`. Every
 * call site used to do `failure instanceof Error ? failure.message : …`, which
 * catches that TypeError and renders an English browser internal to an Arabic
 * speaking waiter — exactly what docs/design forbids ("errors must be
 * human-readable, not technical"). It mattered less when errors were inline
 * notes nobody read; now that they are toasts, they are the thing the user
 * sees when something goes wrong.
 *
 * `ApiError` messages DO pass through: those are the backend's deliberate,
 * typed responses, and the screens branch on `code` before ever reaching here
 * (a stale-close conflict becomes a refresh prompt, not a message).
 */
/**
 * The `reason` discriminator some typed errors carry beside their `code`.
 *
 * `ApiError` keeps the raw body precisely for this: one code can cover several
 * refusals, and occasionally the sentence a person needs depends on which one
 * it was.
 */
function refusalReason(failure: ApiError): string | null {
  const body = failure.body;
  if (body && typeof body === "object" && "reason" in body) {
    const reason = (body as { reason?: unknown }).reason;
    return typeof reason === "string" ? reason : null;
  }
  return null;
}

/**
 * B6 D-39 — what an own delivery still lacks (`missing: ["PHONE", "ADDRESS"]`
 * on a 409 `RESTAURANT_DELIVERY_CONTACT_REQUIRED`); unknown entries dropped.
 */
function deliveryContactMissingOf(failure: ApiError): ("PHONE" | "ADDRESS")[] {
  const body = failure.body;
  if (body && typeof body === "object" && "missing" in body) {
    const missing = (body as { missing?: unknown }).missing;
    if (Array.isArray(missing)) {
      return missing.filter(
        (entry): entry is "PHONE" | "ADDRESS" =>
          entry === "PHONE" || entry === "ADDRESS",
      );
    }
  }
  return [];
}

/**
 * The BOUND a typed refusal was measured against — `minSelect` on an addon
 * group, say. Read beside `reason` so a sentence can quote the number the
 * person has to satisfy instead of a vague "some".
 */
function refusalLimit(failure: ApiError): number | null {
  const body = failure.body;
  if (body && typeof body === "object" && "limit" in body) {
    const limit = (body as { limit?: unknown }).limit;
    return typeof limit === "number" ? limit : null;
  }
  return null;
}

/**
 * The EXISTING supplier a name collision named back (B117/C7).
 *
 * Read off the refusal's own `supplierName`, which is the row already in the
 * store — not the spelling that was just typed. The two differ by
 * construction: the unique index is on the NORMALIZED name, so «مورد QA5
 * الجمله» collides with a stored «مورد QA5 الجملة» and the sentence has to
 * quote the one the owner can actually find in the list.
 */
/**
 * WHICH of the recount's two preconditions refused (B118).
 *
 * The exception publishes a `reason` now, so the till can say the true sentence
 * instead of a disjunction of both. Unknown/absent falls back to the
 * both-conditions wording, which is still true — a payload from a build before
 * the reason existed is the case that needs it.
 */
function recountRefusalReason(failure: ApiError): string | null {
  const body = failure.body;
  if (body && typeof body === "object" && "reason" in body) {
    const reason = (body as { reason?: unknown }).reason;
    return typeof reason === "string" ? reason : null;
  }
  return null;
}

/**
 * A named field of a typed refusal's body — a string, or `null` when the body
 * does not carry it. The inventory refusals (D136–D143) publish their bounds
 * and states this way (`earliest`/`latest`, `status`), and a sentence quotes
 * them rather than a vague "out of range".
 */
function refusalString(failure: ApiError, field: string): string | null {
  const body = failure.body;
  if (body && typeof body === "object" && field in body) {
    const value = (body as Record<string, unknown>)[field];
    return typeof value === "string" && value.trim() !== "" ? value : null;
  }
  return null;
}

/** The numeric twin of `refusalString` (`returnableQuantity`, …). */
function refusalNumber(failure: ApiError, field: string): number | null {
  const body = failure.body;
  if (body && typeof body === "object" && field in body) {
    const value = (body as Record<string, unknown>)[field];
    return typeof value === "number" && Number.isFinite(value) ? value : null;
  }
  return null;
}

function refusalSupplierName(failure: ApiError): string | null {
  const body = failure.body;
  if (body && typeof body === "object" && "supplierName" in body) {
    const name = (body as { supplierName?: unknown }).supplierName;
    return typeof name === "string" && name.trim() !== "" ? name : null;
  }
  return null;
}

/**
 * WHICH BOX a drawer refusal is about (D95, Unit B — B118).
 *
 * All four drawer refusals carry a `label`, and the backend puts it there on
 * purpose: «a till with three drawers on it cannot act on "no drawer is
 * open"». Read the same way as `supplierName` above, and for the same reason —
 * the name has to come from the refusal, not from what this screen believed a
 * moment ago, because the interesting case is precisely the one where the two
 * disagree.
 *
 * `null` for a body that carries none, which the sentences answer with an
 * unnamed twin rather than by inventing a label.
 */
function refusalLabel(failure: ApiError): string | null {
  const body = failure.body;
  if (body && typeof body === "object" && "label" in body) {
    const label = (body as { label?: unknown }).label;
    return typeof label === "string" && label.trim() !== "" ? label : null;
  }
  return null;
}

/**
 * The `subjectId` a typed refusal names — the row the editor should point at.
 *
 * The server resolves it deliberately: `REQUIRED_GROUP_UNSATISFIABLE` raised
 * from a GROUP-level edit walks the dishes attached to that group and reports
 * the one it stranded, precisely so a screen can name it. Throwing that away
 * and saying "the item" left an owner with a refusal and a list of forty
 * dishes to check by hand.
 */
function refusalSubjectId(failure: ApiError): string | null {
  const body = failure.body;
  if (body && typeof body === "object" && "subjectId" in body) {
    const subjectId = (body as { subjectId?: unknown }).subjectId;
    return typeof subjectId === "string" ? subjectId : null;
  }
  return null;
}

/**
 * The `settlementMethod` a refund refusal names.
 *
 * `RESTAURANT_REFUND_METHOD_NOT_REFUNDABLE` covers two different facts and the
 * sentence a cashier needs depends on which: a WRITE_OFF really did take no
 * money, while a method the allowlist has not yet reached is a bill that WAS
 * paid. One sentence for both was wrong the day INSTAPAY shipped.
 */
function refusalSettlementMethod(failure: ApiError): string | null {
  const body = failure.body;
  if (body && typeof body === "object" && "settlementMethod" in body) {
    const method = (body as { settlementMethod?: unknown }).settlementMethod;
    return typeof method === "string" ? method : null;
  }
  return null;
}

/**
 * Is this method refusal the one about a bill that WAS NEVER PAID?
 *
 * Exported, and a pure function of the refusal, because it is the ONLY thing
 * standing between a cashier and a false diagnosis: the "closed unpaid, so
 * there is no money to give back" sentence is true of a walk-out and of nothing
 * else. It said that about an INSTAPAY bill — paid in full, into the bank — for
 * as long as the two shared one message, and the cashier's next move was the
 * untracked drawer payout. A hook cannot be unit-tested in this app (no React
 * test renderer), so the decision lives here where it can be.
 *
 * ONLY `WRITE_OFF`. Not "an unrecognised method probably means unpaid": the
 * whole reason the refusal survives is that a method nobody has decided about
 * must be refused, and such a bill may well have been paid.
 */
export function isUnpaidBillRefusal(failure: ApiError): boolean {
  return refusalSettlementMethod(failure) === "WRITE_OFF";
}

/**
 * What a call site can tell the mapper about ITS OWN screen.
 *
 * Deliberately one optional bag rather than a second hook: every call site
 * raises the same refusals, and only some of them hold the catalogue needed to
 * turn an id into a name. One that does not simply omits it and gets the
 * unnamed sentence, which is the wording that shipped before.
 */
export type ErrorMessageContext = {
  /**
   * Turn a refusal's `subjectId` into something a person recognises. Return
   * `null` for an id this screen's data does not cover — a stale list, or a
   * subject that is not a dish at all (the same code names a GROUP when the
   * refusal came from the override editor).
   */
  resolveSubject?: (subjectId: string) => string | null;
};

/**
 * A settle refused because the bill is ALREADY paid (either flavor). The till
 * treats this like a conflict, not a plain error: the screen is showing an
 * unsettled bill that is in fact settled, so the invoice must refetch and the
 * settle sheet close — a toast alone leaves the sheet open over a paid bill.
 */
export function isAlreadySettledRefusal(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.code === "RESTAURANT_SETTLEMENT_NOT_CORRECTABLE" &&
    (refusalReason(error)?.startsWith("ALREADY_SETTLED") ?? false)
  );
}

/**
 * D141 — the server would not return a plate to stock (`reason` NOT_COOKED |
 * ALREADY_RETURNED). The till offered the choice off a plate state that is no
 * longer true — a remade plate back in QUEUED, another device's void — so the
 * bill refetches and the sheet re-reads which plates are cooked. Nothing
 * applied: the whole command rolled back, claim included, so the cashier can
 * confirm again without the return (a different payload, so a new command id).
 */
export function isItemNotReturnableRefusal(error: unknown): boolean {
  return (
    error instanceof ApiError && error.code === "RESTAURANT_ITEM_NOT_RETURNABLE"
  );
}

/**
 * WHICH addon refusal this is, and the bound it names — the whole decision
 * behind the two sentences the till shows, as a pure function.
 *
 * Extracted rather than left as a ternary inside the hook for the reason this
 * codebase keeps re-learning: a rule asserted only through a rendered toast is
 * a rule no test can localise when it breaks, and there is no hook harness here
 * to render one. `isUnpaidBillRefusal` beside it exists for the same reason.
 *
 * `TOO_FEW` is the only case a waiter reaches by using the till normally — the
 * picker enforces `maxSelect`, and a group the dish does not carry cannot be
 * tapped — so it is the one that earns an instruction. `atLeast` falls back to
 * 1 when the server names no bound: a sentence reading "at least 1" is true of
 * every required group, and one reading "at least undefined" is not a sentence.
 *
 * `null` for anything that is not this refusal, so the caller's own `switch`
 * stays the thing that decides which code it is.
 */
export type AddonSelectionRefusalKind =
  | { kind: "TOO_FEW"; atLeast: number }
  | { kind: "NOT_VALID" };

export function addonSelectionRefusal(
  failure: unknown,
): AddonSelectionRefusalKind | null {
  if (
    !(failure instanceof ApiError) ||
    failure.code !== "RESTAURANT_ADDON_SELECTION_INVALID"
  ) {
    return null;
  }
  if (refusalReason(failure) !== "MIN_SELECT") return { kind: "NOT_VALID" };
  const atLeast = refusalLimit(failure);
  return {
    kind: "TOO_FEW",
    atLeast: atLeast !== null && atLeast > 0 ? atLeast : 1,
  };
}

export function useErrorMessage() {
  const t = useTranslate();
  /**
   * One refusal has TWO right answers, and which one is right depends on who
   * is reading it. `RESTAURANT_SHIFT_REQUIRED` used to say "open a shift
   * first" to everybody — but the drawer screens are `RESTAURANT_SHIFT_MANAGE`
   * and **no preset role carries it** (D24), so the cashier who actually hits
   * this refusal usually cannot act on that sentence at all. A dead
   * instruction is worse than no instruction: it sends the person who can do
   * nothing looking for a screen they will never be allowed to open.
   *
   * So the sentence branches on authority, here rather than in a screen: the
   * toast is raised from several call sites and the refusal means the same
   * thing at every one of them.
   */
  const { data: session } = useSession();
  const canManageShift = hasPermission(session, PERMISSIONS.SHIFT_MANAGE);
  const { locale } = useI18n();
  /*
   * A business DATE from a refusal body (`YYYY-MM-DD`), as a calendar day in
   * the reader's language with Western digits — read in UTC, because it is a
   * date and not an instant (a zone shift would move it a day).
   */
  const calendarDay = (value: string): string =>
    new Intl.DateTimeFormat(locale === "ar" ? "ar-EG-u-nu-latn" : "en-EG", {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(new Date(`${value}T00:00:00.000Z`));
  return (failure: unknown, context?: ErrorMessageContext): string => {
    if (failure instanceof ApiError) {
      /*
       * Typed codes that no screen branches on inline still need to reach an
       * Arabic-speaking cashier in Arabic. The backend's `message` is for
       * logs and API consumers; anything a person can trigger from a till
       * mid-service earns a line here. Codes without a line fall through to
       * the backend message, which is still better than a blank toast.
       */
      switch (failure.code) {
        /*
         * The FIRST refusal a waiter meets, and it used to arrive in English
         * carrying a UUID: tapping a dish with a required option group raised
         * the backend's own `message` on a fully Arabic till, telling nobody
         * what to do (the answer is "tap «+ إضافات» first").
         *
         * THREE cases behind one code, told apart by the `reason` the server
         * now publishes beside it. Only MIN_SELECT is reachable by using the
         * till normally — the picker enforces `maxSelect`, and a group the
         * dish does not carry cannot be selected at all — so it gets the
         * actionable sentence, naming the bound so it reads "choose at least
         * 1" and not "choose some". The other two mean a stale tab or a
         * tampered payload, and their sentence says what to do about THAT
         * rather than pretending the waiter mis-tapped.
         */
        case "RESTAURANT_ADDON_SELECTION_INVALID": {
          const addon = addonSelectionRefusal(failure);
          return addon?.kind === "TOO_FEW"
            ? t((m) => m.serverErrors.addonSelectionTooFew, {
                count: addon.atLeast,
              })
            : t((m) => m.serverErrors.addonSelectionNotValid);
        }
        case "RESTAURANT_INVOICE_HAS_PENDING_ITEMS":
          return t((m) => m.serverErrors.invoiceHasUnreachedItems);
        case "RESTAURANT_INVOICE_EMPTY":
          return t((m) => m.serverErrors.invoiceEmpty);
        // B6 D-39 — an own delivery needs the phone and the address before it
        // is sent to the kitchen or closed; the body names what is missing.
        case "RESTAURANT_DELIVERY_CONTACT_REQUIRED": {
          const missing = deliveryContactMissingOf(failure);
          if (missing.length === 1 && missing[0] === "PHONE") {
            return t((m) => m.dispatch.contactRequiredPhone);
          }
          if (missing.length === 1 && missing[0] === "ADDRESS") {
            return t((m) => m.dispatch.contactRequiredAddress);
          }
          return t((m) => m.dispatch.contactRequiredBoth);
        }
        case "RESTAURANT_KITCHEN_STATION_MISSING":
          return t((m) => m.serverErrors.kitchenStationMissing);
        case "RESTAURANT_STATION_HAS_HISTORY":
          return t((m) => m.serverErrors.stationHasHistory);
        case "RESTAURANT_KITCHEN_STATION_NOT_ALLOWED":
          return t((m) => m.serverErrors.kitchenStationNotAllowed);
        case "RESTAURANT_STATION_LOCKED_BY_ROLE":
          return t((m) => m.serverErrors.stationLockedByRole);
        case "RESTAURANT_FALLBACK_STATION_INACTIVE":
          return t((m) => m.serverErrors.fallbackStationInactive);
        case "RESTAURANT_DEFAULT_STATION_UNDELETABLE":
          return t((m) => m.serverErrors.fallbackStationProtected);
        case "RESTAURANT_BUILT_IN_STATION_UNDELETABLE":
          return t((m) => m.serverErrors.builtInStationProtected);
        case "RESTAURANT_PRINTING_NOT_FOUND":
          return t((m) => m.serverErrors.printingNotFound);
        case "RESTAURANT_PRINTING_REQUEST_INVALID":
          return t((m) => m.serverErrors.printingRequestInvalid);
        case "RESTAURANT_PRINTING_STATE_CONFLICT":
          return t((m) => m.serverErrors.printingStateConflict);
        case "RESTAURANT_PRINT_DEVICE_UNAUTHORIZED":
          return t((m) => m.serverErrors.printDeviceUnauthorized);
        case "RESTAURANT_INVOICE_NOT_CANCELLABLE":
          return t((m) => m.serverErrors.invoiceNotCancellable);
        case "RESTAURANT_INVOICE_NOT_REOPENABLE":
          return t((m) => m.serverErrors.invoiceNotReopenable);
        case "RESTAURANT_SETTLEMENT_SHORT":
          return t((m) => m.serverErrors.settlementShort);
        // B43 — the party edit or the party settlement named a party this bill
        // does not have. Parties are per-INVOICE, so the usual cause is a bill
        // that moved under this device (another till cleared the marks) rather
        // than a typo, and the sentence says to re-read rather than to retype.
        case "RESTAURANT_PARTY_NOT_FOUND":
          return t((m) => m.serverErrors.partyNotFound);
        case "RESTAURANT_PARTY_LABEL_REQUIRED":
          return t((m) => m.serverErrors.partyLabelRequired);
        // B43 — this family is square. The sheet disables its chip and marks
        // it "Paid", so a cashier should never meet this; it stays because a
        // second till can take the same family's money between this device's
        // last read and its tap, and "refused" alone would read as a broken
        // till rather than as the guarantee it is.
        case "RESTAURANT_PARTY_ALREADY_SETTLED":
          return t((m) => m.serverErrors.partyAlreadySettled);
        // D133 fix — "Settle this share" named a claim whose share an earlier
        // settle already took (another till, a double tap). The tender was
        // refused whole, so nothing was charged twice.
        case "RESTAURANT_PAYMENT_SHARE_SETTLED":
          return t((m) => m.serverErrors.paymentShareSettled);
        // B107 — a seated bill has no customer to correct. The till only
        // renders the control on an off-premise bill, so this is a stale view
        // (the bill moved table under this device) rather than a mis-tap, and
        // the sentence says to re-read rather than to retype.
        case "RESTAURANT_CUSTOMER_NOT_EDITABLE":
          return t((m) => m.serverErrors.customerNotEditable);
        // T6 — `editCharges` on a dine-in bill, or towards DINE_IN. Same
        // reading as the customer refusal: the screen is stale, re-read it.
        case "RESTAURANT_CHARGES_NOT_EDITABLE":
          return t((m) => m.serverErrors.chargesNotEditable);
        // D153 — a captain on a bill at no table, or a member who is not this
        // store's waiter. The till offers neither, so both read as a stale
        // screen (a role changed, the bill moved) and say to re-read.
        case "RESTAURANT_CAPTAIN_NOT_DINE_IN":
          return t((m) => m.serverErrors.captainNotDineIn);
        case "RESTAURANT_CAPTAIN_NOT_ELIGIBLE":
          return t((m) => m.serverErrors.captainNotEligible);
        // C2 — a platform on a dine-in bill. The till never sends one, so
        // this is a hand-built request or an older build; still said in words.
        case "RESTAURANT_AGGREGATOR_CHANNEL_INVALID":
          return t((m) => m.serverErrors.aggregatorChannelInvalid);
        // B3 — a write-off is a decision about a WHOLE bill, so it cannot be
        // one tender of several. The sentence names the whole-bill path, which
        // is the thing the cashier actually wants.
        case "RESTAURANT_PARTIAL_WRITE_OFF":
          return t((m) => m.serverErrors.partialWriteOff);
        // B3 — the double-charge fence. A partly tendered bill is CLOSED, which
        // is the status the ordinary Settle opens on, so this fires on a
        // perfectly ordinary tap by a second cashier. The remedy is named,
        // because "refused" alone would send them looking for a broken till.
        case "RESTAURANT_INVOICE_ALREADY_TENDERED":
          return t((m) => m.serverErrors.invoiceAlreadyTendered);
        // D30. `RESTAURANT_SETTLEMENT_NOT_CORRECTABLE` now fires only for
        // NOT_SETTLED (S14/D49 removed the past-business-day refusal — the
        // shift a correction lands against is frozen at close, so there is
        // no window left to cross). `ALREADY_SETTLED_DIFFERENTLY` rides the same code
        // but is the opposite situation — a SETTLE was refused because the
        // bill is already paid another way — so it gets its own sentence, the
        // one that names the command that can actually do what was asked.
        // `ALREADY_SETTLED_SAME_METHOD` is the third rider: same method,
        // different tendered amount. The record stands and NOTHING can change
        // it, so its sentence names no command — pointing at correctSettlement
        // (which changes methods only) would be an instruction that dead-ends.
        case "RESTAURANT_SETTLEMENT_NOT_CORRECTABLE":
          switch (refusalReason(failure)) {
            case "ALREADY_SETTLED_DIFFERENTLY":
              return t(
                (m) => m.serverErrors.settlementAlreadySettledDifferently,
              );
            case "ALREADY_SETTLED_SAME_METHOD":
              return t((m) => m.serverErrors.settlementAlreadySettledSame);
            // B3 — WRITE_OFF as a correction target is refused outside the
            // bill's own business day: the shift it would land against is
            // already frozen at close, so the sentence names the actual
            // limit rather than repeating the generic "not settled" line.
            case "WRITE_OFF_OUTSIDE_BUSINESS_DAY":
              return t((m) => m.serverErrors.writeOffOutsideBusinessDay);
            default:
              return t((m) => m.serverErrors.settlementNotCorrectable);
          }
        /*
         * The refund's own refusals (R1/F3, design §8.4). Every one of these
         * is reachable from a till mid-service by a cashier holding the rung,
         * so every one earns an Arabic sentence rather than the backend's
         * English `message`.
         *
         * `RESTAURANT_REFUND_ITEM_ALREADY_REFUNDED` is raised by TWO paths
         * that deliberately share a code — the cheap read before the write,
         * and the partial unique index that is the actual guarantee. From the
         * cashier's side a double-tap and a lost race are the same event, and
         * reporting them differently would teach a support person to hunt a
         * bug that is not there.
         *
         * The sheet DOES grey out a plate that has already come back — the
         * ledger's per-plate `refunded` flag is on the invoice read (R12/§10)
         * and `pickableRefundItems` uses it — so this sentence is no longer
         * the first thing a second partial refund meets. It is what arrives
         * when two tills open the same bill at once, which only the partial
         * unique index can arbitrate, and it must stay for exactly that.
         */
        case "RESTAURANT_REFUND_INVOICE_NOT_SETTLED":
          return t((m) => m.serverErrors.refundNotSettled);
        /*
         * TWO sentences behind one code, chosen on the method the body names.
         *
         * The WRITE_OFF sentence — "this bill was closed unpaid, so there is
         * no money to give back" — is TRUE of a walk-out and false of anything
         * else. It was the only sentence when the refusal was an allowlist of
         * CASH and CARD, and the day master added INSTAPAY it became a
         * diagnosis on screen that contradicted the bill: paid in full, into
         * the bank, and the till said it was never paid. The till no longer
         * OFFERS a refund it cannot get (`isRefundOfferable` mirrors the
         * server's allowlist), so this arrives from a stale tab or a method a
         * newer server knows about — and it must still be honest.
         */
        case "RESTAURANT_REFUND_METHOD_NOT_REFUNDABLE":
          return isUnpaidBillRefusal(failure)
            ? t((m) => m.serverErrors.refundMethodNotRefundable)
            : t((m) => m.serverErrors.refundMethodNotRefundableOther);
        case "RESTAURANT_REFUND_NOTHING_TO_REFUND":
          return t((m) => m.serverErrors.refundNothingToRefund);
        case "RESTAURANT_REFUND_ITEM_NOT_CHARGEABLE":
          return t((m) => m.serverErrors.refundItemNotChargeable);
        case "RESTAURANT_REFUND_ITEM_ALREADY_REFUNDED":
          return t((m) => m.serverErrors.refundItemAlreadyRefunded);
        case "RESTAURANT_REFUND_DUPLICATE_ITEM":
          return t((m) => m.serverErrors.refundDuplicateItem);
        /*
         * The two reason-gate refusals, and the reason they were missing.
         *
         * Both are raised by the SHARED reason policy (`reason-code.policy`,
         * `RestaurantReasonCodeInvalidException` carries the vocabulary in its
         * body), so they cover a void, a comp and a refund alike — and the
         * sheet gates both client-side, which is exactly why they were assumed
         * unreachable and left to fall through to the server's English. They
         * are reachable: a second tab holding an older bundle, or a picker
         * rendered before a vocabulary changed, sends a code this release's
         * list no longer has. A refusal a cashier cannot read is the same as
         * no refusal at all, and this one lands on an Arabic till.
         */
        case "RESTAURANT_REASON_CODE_INVALID":
          return t((m) => m.serverErrors.reasonCodeInvalid);
        case "RESTAURANT_VOID_REASON_NOTE_REQUIRED":
          return t((m) => m.serverErrors.voidReasonNoteRequired);
        /*
         * The rest of what the refund path can raise and nothing had words
         * for. A refund names a BILL and its PLATES, and either can move under
         * a screen between its last refresh and the cashier's tap
         * (`refundInvoiceInTx` throws the first two before it takes any lock).
         * `RESTAURANT_INVOICE_VERSION_CONFLICT` is deliberately NOT here: the
         * till answers it with the inline recovery card (`isRecoverableConflict`
         * on `InvoicePage`), which is a better answer than a toast and must
         * stay the one that runs.
         */
        case "RESTAURANT_INVOICE_NOT_FOUND":
          return t((m) => m.serverErrors.invoiceNotFound);
        case "RESTAURANT_INVOICE_ITEM_NOT_FOUND":
          return t((m) => m.serverErrors.invoiceItemNotFound);
        // B117/D5 — the refusal that used to arrive as the one above. A remake
        // on a QUEUED plate told the waiter the plate had left the bill and to
        // refresh, which could not help: it was on the bill, and it was the
        // STATE that made the act meaningless.
        case "RESTAURANT_ITEM_NOT_STARTED":
          return t((m) => m.serverErrors.itemNotStarted);
        // B117/RT-D — the recount's clock. The owner made this his blocker at
        // the round table: the Z is recomputed on every read, so amending a
        // drawer after its business day has rolled over changes a figure that
        // was printed, signed and banked against.
        case "RESTAURANT_SHIFT_RECOUNT_WINDOW_CLOSED":
          return t((m) => m.serverErrors.recountWindowClosed);
        /*
         * B117/C8 — absent from this switch, so it fell through to the
         * SERVER'S message and rendered in raw English on the Arabic suppliers
         * screen. The whole reason this switch exists (see its own note above):
         * the backend's `message` is for logs and API consumers, and anything a
         * person can trigger from a till has to reach them in their language.
         *
         * The name comes from the refusal, not from what was typed — B117/C7.
         * Without one the sentence drops the quotation rather than inventing a
         * name, which is the honest shape of "we could not say which".
         */
        case "RESTAURANT_SUPPLIER_NAME_TAKEN": {
          const supplier = refusalSupplierName(failure);
          return supplier === null
            ? t((m) => m.serverErrors.supplierNameTakenUnnamed)
            : t((m) => m.serverErrors.supplierNameTaken, { name: supplier });
        }
        // Minimal purchasing (2026-10-02) — ordering, payables, the photo.
        // Every one reachable from a back-office tap, so every one in words.
        case "RESTAURANT_INGREDIENT_ORDER_NOT_OPEN":
          return t((m) => m.purchasing.errorOrderNotOpen);
        case "RESTAURANT_INGREDIENT_ORDER_NOT_FOUND":
          return t((m) => m.purchasing.errorOrderNotFound);
        case "RESTAURANT_INGREDIENT_ORDER_MISMATCH":
          return t((m) => m.purchasing.errorOrderMismatch);
        case "RESTAURANT_SUPPLIER_INACTIVE":
          return t((m) => m.purchasing.errorSupplierInactive);
        case "RESTAURANT_SUPPLIER_OWED_CHANGED":
          return t((m) => m.purchasing.errorOwedChanged);
        case "RESTAURANT_PURCHASE_NOT_FOUND":
          return t((m) => m.purchasing.errorPurchaseNotFound);
        /*
         * Receiving a delivery (D147). The drawer refusal names BOTH ways
         * out (open a drawer, or record it on account); the duplicate paper
         * names the earlier paper's business date so the person goes and
         * looks at it rather than retyping.
         */
        case "RESTAURANT_DELIVERY_DRAWER_NOT_OPEN":
          return t((m) => m.deliveries.errorDrawerNotOpen);
        case "RESTAURANT_DELIVERY_DUPLICATE_PAPER": {
          const earlier = refusalString(failure, "earlierBusinessDate");
          return earlier
            ? t((m) => m.deliveries.errorDuplicatePaper, {
                date: calendarDay(earlier),
              })
            : t((m) => m.deliveries.errorDuplicatePaperUndated);
        }
        case "RESTAURANT_DELIVERY_VAT_WITHOUT_TAX_INVOICE":
          return t((m) => m.deliveries.errorVatWithoutTaxInvoice);
        case "RESTAURANT_DELIVERY_PACK_INVALID":
          return t((m) => m.deliveries.errorPackInvalid);
        case "RESTAURANT_DELIVERY_DRAWER_TOTAL_ZERO":
          return t((m) => m.deliveries.errorDrawerTotalZero);
        case "RESTAURANT_DELIVERY_PHOTO_ALREADY_ATTACHED":
          return t((m) => m.deliveries.errorPhotoAlreadyAttached);
        case "RESTAURANT_DELIVERY_NOT_FOUND":
          return t((m) => m.deliveries.errorNotFound);
        case "RESTAURANT_INGREDIENT_PACK_HINT_INVALID":
          return t((m) => m.ingredient.packPairRequired);
        /*
         * Inventory item card change (D136–D143). Every typed refusal those
         * phases added gets its sentence here, so a screen that does not
         * branch on one still says it in the reader's language
         * (`docs/contracts/ux-error-localization-contract-v1.md`).
         *
         * D136: the restock form mirrors the window, but the server's today
         * decides — a form left open across the roll-over, or a device whose
         * business day is a day off, meets this refusal, and the sentence
         * quotes the window the server measured against.
         */
        case "RESTAURANT_PURCHASE_DATE_OUT_OF_RANGE": {
          const earliest = refusalString(failure, "earliest");
          const latest = refusalString(failure, "latest");
          return earliest && latest
            ? t((m) => m.serverErrors.purchaseDateOutOfRange, {
                from: calendarDay(earliest),
                to: calendarDay(latest),
              })
            : t((m) => m.serverErrors.purchaseDateOutOfRangeUnbounded);
        }
        // D138 — the form applies the sign itself, so this is a stale build
        // or a hand-made request; the sentence still says the rule.
        case "RESTAURANT_ADJUSTMENT_SIGN_INVALID":
          return t((m) => m.serverErrors.adjustmentSignInvalid);
        // D142 — the body carries what may still go back.
        case "RESTAURANT_SUPPLIER_RETURN_EXCEEDS_PURCHASE": {
          const returnable = refusalNumber(failure, "returnableQuantity");
          return returnable === null
            ? t((m) => m.serverErrors.supplierReturnExceedsPurchaseUnnamed)
            : t((m) => m.serverErrors.supplierReturnExceedsPurchase, {
                quantity: returnable,
              });
        }
        // D141 — comp never returns to stock; a void/refund names its plate's
        // reason.
        case "RESTAURANT_RETURN_TO_STOCK_NOT_ALLOWED":
          return t((m) => m.serverErrors.returnToStockNotAllowed);
        // D163 — the settle windows send a reference with CARD/INSTAPAY only,
        // so this is a stale build or a hand-made request.
        case "RESTAURANT_TENDER_REFERENCE_NOT_ALLOWED":
          return t((m) => m.serverErrors.tenderReferenceNotAllowed);
        case "RESTAURANT_ITEM_NOT_RETURNABLE":
          switch (refusalReason(failure)) {
            case "NOT_COOKED":
              return t((m) => m.serverErrors.itemNotReturnableNotCooked);
            case "ALREADY_RETURNED":
              return t((m) => m.serverErrors.itemNotReturnableAlreadyReturned);
            default:
              return t((m) => m.serverErrors.itemNotReturnable);
          }
        // D143 — stocktake states.
        case "RESTAURANT_STOCKTAKE_ALREADY_OPEN":
          return t((m) => m.serverErrors.stocktakeAlreadyOpen);
        case "RESTAURANT_STOCKTAKE_NOT_OPEN":
          switch (refusalString(failure, "status")) {
            case "POSTED":
              return t((m) => m.serverErrors.stocktakeNotOpenPosted);
            case "CANCELLED":
              return t((m) => m.serverErrors.stocktakeNotOpenCancelled);
            default:
              return t((m) => m.serverErrors.stocktakeNotOpen);
          }
        case "RESTAURANT_STOCKTAKE_NOT_FOUND":
          return t((m) => m.serverErrors.stocktakeNotFound);
        // D135 — a refusal, never a truncation (the CSV rule): narrow it.
        case "RESTAURANT_ITEM_CARD_TOO_LARGE":
          return t((m) => m.serverErrors.itemCardTooLarge);
        // Client-minted ids make this practically unreachable; it still gets
        // words (the argument the command-id conflict below already lost).
        case "RESTAURANT_LEDGER_ID_CONFLICT":
          return t((m) => m.serverErrors.ledgerIdConflict);
        case "RESTAURANT_INGREDIENT_NOT_FOUND":
          return t((m) => m.serverErrors.ingredientNotFound);
        case "RESTAURANT_INVOICE_PHOTO_INVALID":
          switch (refusalReason(failure)) {
            case "TYPE":
              return t((m) => m.purchasing.photoWrongType);
            case "SIZE":
              return t((m) => m.purchasing.photoTooLarge);
            case "KEY":
              return t((m) => m.purchasing.photoNotYours);
            default:
              return t((m) => m.purchasing.photoUploadFailed);
          }
        // Client-minted ids make this practically unreachable, which is why it
        // had no sentence; "practically unreachable" is the same argument that
        // left the two above in English, and it was wrong there too.
        case "RESTAURANT_COMMAND_OUTCOME_UNCERTAIN":
          return t((m) => m.serverErrors.commandOutcomeUncertain);
        case "RESTAURANT_COMMAND_ID_CONFLICT":
          return t((m) => m.serverErrors.commandIdConflict);
        // P9 — a command id past the 7-day replay window: nothing ran now.
        // The invoice screen answers it with the replayed card instead.
        case "RESTAURANT_COMMAND_EXPIRED":
          return t((m) => m.serverErrors.commandExpired);
        // P9.3 — the request's own clock is as old as its id: not a late
        // replay but this device's date and time, which a refresh cannot fix.
        case "RESTAURANT_DEVICE_CLOCK_WRONG":
          return t((m) => m.serverErrors.deviceClockWrong);
        // A FAULT, not a refusal (design §1.3): the ledger's own CHECK
        // constraints are unreachable from the refund handler, so reaching one
        // is a bug in the product rather than anything the cashier did. Named
        // rather than left as raw English, and the sentence says the money did
        // NOT move — which is the only thing the person at the till needs.
        case "RESTAURANT_MONEY_LEDGER_REJECTED":
          return t((m) => m.serverErrors.moneyLedgerRejected);
        // The same class as the row above and mapped for the same reason: the
        // allocation policy's two preconditions are proven unreachable from the
        // refund handler, so meeting one is a fault in the product — but a
        // fault still lands on an Arabic till, and leaving these two as the
        // only refund codes that fall through to the backend's English while
        // `MONEY_LEDGER_REJECTED` beside them has a sentence was a split with
        // no rule behind it.
        case "RESTAURANT_REFUND_ALLOCATION_BASIS_INVALID":
          return t((m) => m.serverErrors.refundAllocationBasisInvalid);
        case "RESTAURANT_REFUND_ALLOCATION_BASIS_MISMATCH":
          return t((m) => m.serverErrors.refundAllocationBasisMismatch);
        /*
         * F24 (§5.12) — the customer's own request for a bag. Both reachable
         * from the till's strip, so both in words. `NOT_REQUESTED` is the
         * race the strip's revision refresh cannot close: a second till accepted it,
         * the customer cancelled it, or it expired between the read and the
         * tap. The sentence says to re-read, because the card is already
         * gone from the truth and only stale on this screen; the strip
         * refetches on it too. `ORDERING_OFF` is the storefront's refusal
         * and should never reach a till — but an owner switching ordering
         * off does NOT stop an accept of what already arrived, so if it ever
         * does arrive here it is a server-side change, said honestly.
         */
        case "RESTAURANT_ONLINE_ORDER_NOT_REQUESTED":
          return t((m) => m.serverErrors.onlineOrderNotRequested);
        case "RESTAURANT_ONLINE_ORDERING_OFF":
          return t((m) => m.serverErrors.onlineOrderingOff);
        /*
         * A dish 86'd or removed between the customer's request and the tap:
         * the accept rolled back whole (no bill, no sequence) and the card
         * stays. The sentence names what to do — reject, or ring the customer —
         * rather than the backend's product id (review 2026-10-07, finding 12).
         */
        case "RESTAURANT_MENU_ITEM_UNAVAILABLE":
          return t((m) => m.serverErrors.menuItemUnavailable);
        case "RESTAURANT_TABLE_OCCUPIED":
          return t((m) => m.serverErrors.tableOccupied);
        case "RESTAURANT_INVOICE_NOT_OPEN":
          return t((m) => m.serverErrors.invoiceNotOpen);
        // D172 — a reprint of a ticket whose every plate is voided. Nothing
        // was sent, so the till must not say the paper went to the kitchen.
        case "RESTAURANT_TICKET_NOTHING_TO_PRINT":
          return t((m) => m.serverErrors.ticketNothingToPrint);
        // D167 — a second «تراجع» after the window, or after another device
        // took the tap back, reached the Arabic wall in the server's English.
        case "RESTAURANT_UNDO_UNAVAILABLE":
          return t((m) => m.serverErrors.undoUnavailable);
        // D173 — the settings pages say this inline; anywhere else it is a toast.
        case "RESTAURANT_READY_AGE_OFF_INVALID": {
          const pass = refusalNumber(failure, "targetPassMinutes");
          return pass !== null
            ? t((m) => m.serverErrors.readyAgeOffInvalid, { minutes: String(pass) })
            : t((m) => m.serverErrors.readyAgeOffInvalidUnnamed);
        }
        // P7 R2 — `ChitPrintJob` re-reads on this itself; any other path says it.
        case "RESTAURANT_TICKET_PAPER_CHANGED":
          return t((m) => m.serverErrors.ticketPaperChanged);
        /*
         * D146 — every refusal the discount at the bill can meet, in words.
         * The sheet previews the arithmetic ones and the bill-level ones, so a
         * till reaches these only when the bill moved under it (a plate
         * voided, money taken) or on a build that predates the rule.
         */
        case "RESTAURANT_BILL_DISCOUNT_AGGREGATOR":
          return t((m) => m.serverErrors.billDiscountAggregator);
        case "RESTAURANT_BILL_DISCOUNT_PRICE_TIER":
          return t((m) => m.serverErrors.billDiscountPriceTier);
        case "RESTAURANT_BILL_DISCOUNT_OVER_CAP": {
          const maxPct = refusalNumber(failure, "maxPct");
          const maxAmount = refusalNumber(failure, "maxAmount");
          return maxPct !== null && maxAmount !== null
            ? t((m) => m.serverErrors.billDiscountOverCap, {
                percent: String(maxPct),
                amount: maxAmount.toFixed(2),
              })
            : t((m) => m.serverErrors.billDiscountOverCapUnnamed);
        }
        case "RESTAURANT_BILL_DISCOUNT_BELOW_PAID": {
          const paid = refusalNumber(failure, "tendered");
          return paid !== null
            ? t((m) => m.serverErrors.billDiscountBelowPaid, {
                paid: paid.toFixed(2),
              })
            : t((m) => m.serverErrors.billDiscountBelowPaidUnnamed);
        }
        case "RESTAURANT_BILL_DISCOUNT_WHOLE_BILL":
          return t((m) => m.serverErrors.billDiscountWholeBill);
        case "RESTAURANT_BILL_DISCOUNT_NOTHING_OFF":
          return t((m) => m.serverErrors.billDiscountNothingOff);
        case "RESTAURANT_BILL_DISCOUNT_REASON_INVALID":
          return t((m) => m.serverErrors.billDiscountReasonInvalid);
        case "RESTAURANT_BILL_DISCOUNT_NOTE_REQUIRED":
          return t((m) => m.serverErrors.billDiscountNoteRequired);
        /*
         * D156 — the approval sheet answers these itself (it stays open on a
         * wrong password); the sentences are for any surface that meets one
         * outside the gate.
         */
        case "RESTAURANT_APPROVAL_REQUIRED":
          return t((m) => m.serverErrors.approvalRequired);
        case "RESTAURANT_APPROVAL_INVALID":
          return t((m) => m.serverErrors.approvalInvalid);
        case "RESTAURANT_APPROVAL_LOCKED":
          return t((m) => m.serverErrors.approvalLocked);
        case "RESTAURANT_APPROVAL_PASSWORD_CHANGE_REQUIRED":
          return t((m) => m.serverErrors.approvalPasswordChangeRequired);
        // The cash-drawer shift feature (D25/D29). `RESTAURANT_SHIFT_REQUIRED`
        // is the settle-time refusal the till hint (SettleSheet's `warning`)
        // exists to head off; the other two are the shift screen's own
        // double-tap guards (D23's partial index, D29's status-predicated
        // close).
        case "RESTAURANT_SHIFT_REQUIRED":
          return canManageShift
            ? t((m) => m.serverErrors.shiftRequired)
            : t((m) => m.serverErrors.shiftRequiredAskManager);
        case "RESTAURANT_SHIFT_ALREADY_OPEN":
          return t((m) => m.serverErrors.shiftAlreadyOpen);
        case "RESTAURANT_SHIFT_TRACKING_DISABLED":
          return t((m) => m.serverErrors.shiftTrackingDisabled);
        case "RESTAURANT_SHIFT_NOT_OPEN":
          return t((m) => m.serverErrors.shiftNotOpen);
        /*
         * B118 — EVERY REFUSAL THE NEW DRAWER SURFACE RAISES, IN ARABIC.
         *
         * The four rows above are the drawer codes that existed when this
         * switch was last read; D99, D95, Unit B, Unit D and the second round
         * table added nine more, and not one of them had a line here. So the
         * Arabic till showed «This drawer has already been recounted once» —
         * captured verbatim on a cashier's screen — and eight more like it.
         * The rule was already written at the top of this switch ("anything a
         * person can trigger from a till mid-service earns a line here"); what
         * was missing was somebody applying it to the codes that shipped after
         * it was written.
         *
         * `RESTAURANT_SHIFT_ALREADY_OPEN` is not repeated: it is already
         * mapped above, and the drawer-label collision is a DIFFERENT refusal
         * (`RESTAURANT_DRAWER_ALREADY_OPEN`) with a different remedy — the
         * backend keeps them apart on purpose, and so does this.
         */
        case "RESTAURANT_SHIFT_ALREADY_RECOUNTED":
          return t((m) => m.serverErrors.shiftAlreadyRecounted);
        /*
         * AND THIS ONE NAMED THE WRONG REASON (B118).
         *
         * `recountShift` throws `RECOUNT_UNAVAILABLE` from TWO guards — a
         * drawer with no blind first count, and a drawer the caller did not
         * close — and the exception carries ONE message, the first guard's.
         * Measured: an owner recounting a box a manager had closed five
         * minutes earlier, `first_counted_cash` 258.53 plainly on the row, was
         * told the drawer «was counted before blind counting existed». That is
         * worse than untranslated, because it describes a data fault that is
         * not there and the manager's next move is to report one.
         *
         * The refusal publishes no `reason` to branch on, so the sentence
         * names BOTH preconditions instead of picking one — the same shape the
         * server itself uses for `MANAGER_COUNT_UNAVAILABLE`, which covers two
         * cases in one honest sentence. Giving the exception a `reason`
         * (`NO_FIRST_COUNT` | `NOT_YOUR_DRAWER`) the way
         * `RESTAURANT_SUBSTITUTION_INVALID` already publishes one is the
         * better fix and is a BACKEND change; until it exists, a sentence that
         * states both conditions is true, and one that states the wrong one is
         * not.
         */
        case "RESTAURANT_SHIFT_RECOUNT_UNAVAILABLE": {
          // The backend half landed (B118): branch on it, and keep the
          // both-conditions sentence for a payload that predates the reason.
          const why = recountRefusalReason(failure);
          if (why === "NO_FIRST_COUNT")
            return t((m) => m.serverErrors.recountNoFirstCount);
          if (why === "NOT_YOUR_DRAWER")
            return t((m) => m.serverErrors.recountNotYourDrawer);
          return t((m) => m.serverErrors.recountUnavailable);
        }
        case "RESTAURANT_SHIFT_ALREADY_MANAGER_COUNTED":
          return t((m) => m.serverErrors.shiftAlreadyManagerCounted);
        case "RESTAURANT_SHIFT_MANAGER_COUNT_UNAVAILABLE":
          return t((m) => m.serverErrors.managerCountUnavailable);
        // The note field's own guard. The panels disable their save on an empty
        // box, so this is a stale tab or a second tap — and it still lands on an
        // Arabic screen.
        case "RESTAURANT_SHIFT_NOTE_EMPTY":
          return t((m) => m.serverErrors.shiftNoteEmpty);
        /*
         * D102's OTHER arm (QA round 4). This used to come back as
         * `SELLER_PERMISSION_DENIED` naming two report grants, so a cashier who
         * had simply picked the wrong drawer was told her role was short a
         * permission — one she does not need and succeeds without on her own
         * box a second later.
         */
        case "RESTAURANT_SHIFT_NOTE_NOT_YOUR_DRAWER":
          return t((m) => m.serverErrors.shiftNoteNotYourDrawer);
        /*
         * The four handover refusals (D95, Unit B). Each names the box, because
         * the server sends the label for exactly that: on a counter with three
         * drawers, "no drawer is open" is not something a cashier can act on.
         * The unnamed twin is for a body that arrives without one — an older
         * build, or a proxy that dropped the field — and says "that drawer"
         * rather than inventing a name.
         */
        case "RESTAURANT_DRAWER_ALREADY_OPEN": {
          const label = refusalLabel(failure);
          return label === null
            ? t((m) => m.serverErrors.drawerAlreadyOpenUnnamed)
            : t((m) => m.serverErrors.drawerAlreadyOpen, { label });
        }
        case "RESTAURANT_DRAWER_IS_YOURS": {
          const label = refusalLabel(failure);
          return label === null
            ? t((m) => m.serverErrors.drawerIsYoursUnnamed)
            : t((m) => m.serverErrors.drawerIsYours, { label });
        }
        case "RESTAURANT_DRAWER_NOT_OPEN": {
          const label = refusalLabel(failure);
          return label === null
            ? t((m) => m.serverErrors.drawerNotOpenUnnamed)
            : t((m) => m.serverErrors.drawerNotOpen, { label });
        }
        case "RESTAURANT_DRAWER_ALREADY_TAKEN_OVER": {
          const label = refusalLabel(failure);
          return label === null
            ? t((m) => m.serverErrors.drawerAlreadyTakenOverUnnamed)
            : t((m) => m.serverErrors.drawerAlreadyTakenOver, { label });
        }
        /*
         * The last open drawer over live bills (founder decision, 2026-10-03).
         * The close dialog pre-empts it, with the count, from the drawer list
         * and the summary; this is the stale-screen case.
         */
        case "RESTAURANT_LAST_DRAWER_HAS_OPEN_INVOICES":
          return t((m) => m.serverErrors.lastDrawerHasOpenInvoices);
        /*
         * The custody movement's five (Unit E). The form pre-empts every one
         * of them — the category picker offers only the categories that pair
         * with the chosen kind, the name picker lists this store's staff and
         * leaves the cashier herself out of it — so each of these means a
         * stale tab or a person who left the team between the list and the
         * tap. Mapped anyway, by the same argument the two codes above them
         * settled: "practically unreachable" is what left a refusal in English
         * the last time, and it was wrong then.
         */
        case "RESTAURANT_MOVEMENT_CATEGORY_INVALID":
          return t((m) => m.serverErrors.movementCategoryInvalid);
        case "RESTAURANT_MOVEMENT_COUNTERPARTY_REQUIRED":
          return t((m) => m.serverErrors.movementCounterpartyRequired);
        case "RESTAURANT_MOVEMENT_COUNTERPARTY_NOT_ALLOWED":
          return t((m) => m.serverErrors.movementCounterpartyNotAllowed);
        case "RESTAURANT_MOVEMENT_COUNTERPARTY_IS_YOU":
          return t((m) => m.serverErrors.movementCounterpartyIsYou);
        case "RESTAURANT_MOVEMENT_COUNTERPARTY_UNKNOWN":
          return t((m) => m.serverErrors.movementCounterpartyUnknown);
        /*
         * D157 — the optional receipt photo. The screens offer «إرفاق صورة»
         * only to the people who may, so these are a stale tab, a race with a
         * colleague's attach, or a guessed id — mapped anyway.
         */
        case "RESTAURANT_MOVEMENT_PHOTO_ALREADY_ATTACHED":
          return t((m) => m.shift.movementPhotoErrorAlreadyAttached);
        case "RESTAURANT_MOVEMENT_PHOTO_NOT_ALLOWED":
          return refusalString(failure, "act") === "VIEW"
            ? t((m) => m.shift.movementPhotoErrorViewNotAllowed)
            : t((m) => m.shift.movementPhotoErrorNotAllowed);
        case "RESTAURANT_MOVEMENT_NOT_FOUND":
          return t((m) => m.shift.movementPhotoErrorNotFound);
        // B117/Q6 — covers after the close are a supervisor's correction. The
        // till now offers the control on the rung the server checks, so this is
        // the race (the bill settled under the screen) and the waiter's own
        // stale view — both of which still have to read in Arabic.
        case "RESTAURANT_GUEST_COUNT_LOCKED":
          return t((m) => m.serverErrors.guestCountLocked);
        /*
         * NOT a restaurant code, and that is why it had no line: the seller
         * guard's own refusal, whose message is the English «Missing required
         * permissions». It reaches this app on every rung a preset does not
         * carry — `SHIFT_MANAGE` is not on every role (D24/D28), so a cashier
         * meets it on the drawer screens themselves.
         */
        case "SELLER_PERMISSION_DENIED":
          return t((m) => m.serverErrors.permissionDenied);
        // D40 — the export's row ceiling is a REFUSAL, never a silent
        // truncation (§4's rule): the sentence names the remedy so the
        // reader narrows the range instead of re-trying the same request.
        case "RESTAURANT_EXPORT_TOO_LARGE":
          return t((m) => m.serverErrors.exportTooLarge);
        // D47/B4 — the till's picker disables an out-of-window tier and
        // pre-selects the auto-apply one, but it mirrors `isActiveNow`
        // rather than deciding it, so a stale read or clock skew can still
        // reach this refusal. Three reasons, three sentences.
        /**
         * D55/D56 — one code, five refusals, and the server's `message` is
         * English prose. Falling through to `default` put that English in
         * front of an Arabic owner on the one screen where the refusal is the
         * whole feedback. `DUPLICATE` is raised for a repeated OPTION in an
         * override set AND for a repeated INGREDIENT in the costing save, so
         * its sentence names a repeated ROW rather than either one.
         *
         * The `default` is deliberate rather than an oversight: a reason
         * added server-side after this build ships still has to arrive
         * translated, and a vague Arabic sentence beats a precise English one.
         */
        /**
         * D61–D63 — the swap refusals. Same shape and same reason as the
         * addon block below it: the server's `message` is English prose, and
         * this refusal arrives while a waiter is STANDING AT A TABLE having
         * just told a guest yes.
         *
         * Two of them are reachable from a correct-looking screen and cannot
         * be pre-empted by the picker, because the collision lives on the
         * OPTION the guest picks after the swap dialog closes: an option that
         * already replaces the ingredient being swapped out
         * (`DUPLICATE_REMOVAL`), or one that adds what the swap removes
         * (`CHAINED_SWAP`). Both sentences therefore say what to do about it,
         * not merely what happened.
         */
        case "RESTAURANT_SUBSTITUTION_INVALID":
          switch (refusalReason(failure)) {
            case "SAME_INGREDIENT":
              return t((m) => m.serverErrors.swapSameIngredient);
            case "DUPLICATE_REMOVAL":
              return t((m) => m.serverErrors.swapDuplicateRemoval);
            case "CHAINED_SWAP":
              return t((m) => m.serverErrors.swapChainedSwap);
            case "INGREDIENT_NOT_FOUND":
              return t((m) => m.serverErrors.swapIngredientNotFound);
            case "NOT_ON_RECIPE":
              return t((m) => m.serverErrors.swapNotOnRecipe);
            case "UNIT_MISMATCH":
              return t((m) => m.serverErrors.swapUnitMismatch);
            case "NOT_SUPPORTED_ON_COMBO":
              return t((m) => m.serverErrors.swapNotSupportedOnCombo);
            /* B19's two, and the only ones here that are about the PLATE
               rather than about an ingredient. `PLATE_ALREADY_STARTED` is the
               one a waiter will actually meet — a cook tapped start while they
               were still choosing — so its sentence carries the way out (void
               it and re-order) rather than only the refusal. */
            case "PLATE_ALREADY_STARTED":
              return t((m) => m.serverErrors.swapPlateAlreadyStarted);
            case "PLATE_NOT_AMENDABLE":
              return t((m) => m.serverErrors.swapPlateNotAmendable);
            default:
              return t((m) => m.serverErrors.swapInvalid);
          }
        case "RESTAURANT_ADDON_OVERRIDE_INVALID":
          switch (refusalReason(failure)) {
            case "GROUP_NOT_ATTACHED":
              return t((m) => m.serverErrors.addonOverrideGroupNotAttached);
            case "ADDON_NOT_IN_GROUP":
              return t((m) => m.serverErrors.addonOverrideAddonNotInGroup);
            case "DUPLICATE":
              return t((m) => m.serverErrors.addonOverrideDuplicateRow);
            case "REQUIRED_GROUP_UNSATISFIABLE": {
              // Named where the screen can name it. The subject is a PRODUCT
              // when the refusal came from a group-level edit — a group rule
              // tightened, or an option paused, stranding some dish elsewhere
              // in the menu — and that dish is the whole point of the message.
              // It is an addon GROUP when the refusal came from the per-item
              // override editor, where the dish is already the screen you are
              // on; there the resolver returns null and the wording is
              // unchanged.
              const subjectId = refusalSubjectId(failure);
              const name = subjectId
                ? (context?.resolveSubject?.(subjectId) ?? null)
                : null;
              return name
                ? t(
                    (m) =>
                      m.serverErrors.addonOverrideRequiredUnsatisfiableNamed,
                    { name },
                  )
                : t((m) => m.serverErrors.addonOverrideRequiredUnsatisfiable);
            }
            case "INGREDIENT_NOT_FOUND":
              return t((m) => m.serverErrors.addonOverrideIngredientNotFound);
            default:
              return t((m) => m.serverErrors.addonOverrideInvalid);
          }
        case "RESTAURANT_PRICE_TIER_UNAVAILABLE":
          switch (refusalReason(failure)) {
            case "INACTIVE":
              return t((m) => m.serverErrors.priceTierInactive);
            case "OUTSIDE_WINDOW":
              return t((m) => m.serverErrors.priceTierOutsideWindow);
            default:
              return t((m) => m.serverErrors.priceTierNotFound);
          }
        default:
          return failure.message;
      }
    }
    // `fetch` rejects with a TypeError when it cannot reach the host at all.
    if (failure instanceof TypeError) {
      return t((m) => m.common.networkError);
    }
    return t((m) => m.common.error);
  };
}
